import React, { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronDown, Crown, ShieldCheck, Pencil, Tag, Share2, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import SourceBadge from "../SourceBadge"
import { money } from "../../utils/parsers/common"
import {
  useUpdateSavedTourMutation,
  useApplySavedTourDiscountMutation,
  useRemoveSavedTourDiscountMutation,
} from "../../../../../app/services/book/saved_tours"
import EditTourModal from "../EditTourModal"
import DiscountModal from "../DiscountModal"
import ShareModal from "../ShareModal/ShareModal"

const OfferPrice = ({ offer, currency, size = "lg" }) => {
  const displayCurrency = offer.currency || currency
  const priceClass =
    size === "lg" ? "text-2xl text-[#26303D] tracking-tight" : "text-xs font-mono text-[#5A6779]"

  if (!offer.has_discount) {
    return (
      <div
        className={priceClass}
        style={
          size === "lg" ? { fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600 } : undefined
        }
      >
        {money(offer.price, displayCurrency)}
      </div>
    )
  }

  const label =
    offer.discount_type === "percent"
      ? `-${Number(offer.discount_value) % 1 === 0 ? offer.discount_value : Number(offer.discount_value).toFixed(1)}%`
      : `-${money(offer.discount_value, displayCurrency)}`

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5">
        <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-white bg-[#E0554F] rounded-full px-1.5 py-0.5 whitespace-nowrap">
          <Tag size={9} /> {label}
        </span>
        <span className="text-[11px] text-[#9AA5B4] line-through font-mono">
          {money(offer.price, displayCurrency)}
        </span>
      </div>
      <div
        className={priceClass}
        style={
          size === "lg" ? { fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600 } : undefined
        }
      >
        {money(offer.final_price, displayCurrency)}
      </div>
    </div>
  )
}

// Shared icon-button styling so actions read as real controls, not decoration.
const ACTION_BTN_BASE =
  "inline-flex items-center justify-center rounded-lg border border-transparent text-[#7C8697] bg-white/0 transition-colors hover:bg-[#F4F6F9] hover:border-[#E7EAEF] hover:text-[#26303D] active:scale-95"

const ActionIconButton = ({ icon: Icon, size, iconSize, active, activeClass, onClick, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    className={`${ACTION_BTN_BASE} ${size} ${active ? activeClass : ""}`}
  >
    <Icon size={iconSize} strokeWidth={2} />
  </button>
)

const buildTourLink = (sessionKey, tourId) => {
  if (!sessionKey || !tourId) return ""
  return `${window.location.origin}/shared_tours/${sessionKey}#tour-${tourId}`
}

const OffersList = ({ tour, offers, currency, editable = false, onChanged, sessionKey }) => {
  const { t } = useTranslation()

  const [expanded, setExpanded] = useState(false)
  const [activeId, setActiveId] = useState(offers[0].price_id)
  const [editTarget, setEditTarget] = useState(null)
  const [discountTarget, setDiscountTarget] = useState(null)
  const [shareOfferId, setShareOfferId] = useState(null)

  const active = offers.find((o) => o.price_id === activeId) || offers[0]
  const rest = offers.filter((o) => o.price_id !== offers[0].price_id)

  const [updateTour] = useUpdateSavedTourMutation()
  const [applyDiscount] = useApplySavedTourDiscountMutation()
  const [removeDiscount] = useRemoveSavedTourDiscountMutation()

  useEffect(() => {
    setActiveId(offers[0].price_id)
    setExpanded(false)
  }, [offers])

  const buildEditTarget = (offer) => ({ ...tour, ...offer })

  const handleEditSubmit = async (form) => {
    await updateTour({ id: editTarget.id, ...form })
    setEditTarget(null)
    onChanged?.()
  }

  const handleDiscountSubmit = async ({ discount_type, discount_value }) => {
    await applyDiscount({ id: discountTarget.id, discount_type, discount_value })
    setDiscountTarget(null)
    onChanged?.()
  }

  const handleDiscountRemove = async () => {
    await removeDiscount(discountTarget.id)
    setDiscountTarget(null)
    onChanged?.()
  }

  return (
    <div>
      <AnimatePresence mode="wait">
        <motion.div
          key={active.price_id}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.15 }}
        >
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mb-1 min-w-0">
            <SourceBadge sourceName={active.source_name} />
            <div className="flex items-center gap-1 shrink-0">
              {active.price_id === offers[0].price_id && offers.length > 1 && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[#E8871E] whitespace-nowrap mr-1">
                  <Crown size={11} /> {t("saved_tours.best_price")}
                </span>
              )}

              {editable && (
                <>
                  <ActionIconButton
                    icon={Share2}
                    size="w-8 h-8"
                    iconSize={15}
                    title={t("saved_tours.share.title")}
                    onClick={() => setShareOfferId(active.id)}
                  />
                  <ActionIconButton
                    icon={Pencil}
                    size="w-8 h-8"
                    iconSize={15}
                    title={t("saved_tours.edit_tour")}
                    onClick={() => setEditTarget(buildEditTarget(active))}
                  />
                  <ActionIconButton
                    icon={Tag}
                    size="w-8 h-8"
                    iconSize={15}
                    title={t("saved_tours.discount")}
                    active={active.has_discount}
                    activeClass="!text-[#E0554F] !border-[#F3C9C7] !bg-[#FDF1F0]"
                    onClick={() => setDiscountTarget(active)}
                  />
                </>
              )}
            </div>
          </div>

          <OfferPrice offer={active} currency={currency} size="lg" />

          <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-[11px] text-[#9AA5B4] mt-0.5">
            {active.guaranteed && (
              <span className="inline-flex items-center gap-0.5 text-[#2E86DE] whitespace-nowrap">
                · <ShieldCheck size={11} /> {t("saved_tours.guaranteed")}
              </span>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {active.on_request && (
        <div className="mt-3 w-full text-center py-2 rounded-xl border border-[#FFA94D] text-[#E8871E] text-sm font-medium">
          {t("saved_tours.on_request")}
        </div>
      )}

      {rest.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1 text-xs text-[#5A6779] hover:text-[#FF6B3D]"
          >
            <ChevronDown
              size={13}
              className={`transition-transform ${expanded ? "rotate-180" : ""}`}
            />
            {expanded
              ? t("saved_tours.hide")
              : t("saved_tours.more_offers", {
                  count: rest.length,
                })}
          </button>
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-2 flex flex-col gap-1.5">
                  {rest.map((o) => (
                    <div
                      key={o.price_id}
                      className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg border transition-colors min-w-0 ${activeId === o.price_id ? "border-[#FF6B3D] bg-white" : "border-transparent hover:bg-white/60"}`}
                    >
                      <button
                        onClick={() => setActiveId(o.price_id)}
                        className="min-w-0 truncate text-left flex-1"
                      >
                        <SourceBadge sourceName={o.source_name} />
                      </button>

                      <OfferPrice offer={o} currency={currency} size="sm" />

                      <div className="flex items-center gap-0.5 shrink-0">
                        <ActionIconButton
                          icon={Share2}
                          size="w-7 h-7"
                          iconSize={13}
                          title={t("saved_tours.share.title")}
                          onClick={() => setShareOfferId(o.id)}
                        />
                        {editable && (
                          <>
                            <ActionIconButton
                              icon={Pencil}
                              size="w-7 h-7"
                              iconSize={13}
                              title={t("saved_tours.edit_tour")}
                              onClick={() => setEditTarget(buildEditTarget(o))}
                            />
                            <ActionIconButton
                              icon={Tag}
                              size="w-7 h-7"
                              iconSize={13}
                              title={t("saved_tours.discount")}
                              active={o.has_discount}
                              activeClass="!text-[#E0554F] !border-[#F3C9C7] !bg-[#FDF1F0]"
                              onClick={() => setDiscountTarget(o)}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {editable && (
        <>
          <EditTourModal
            open={!!editTarget}
            tour={editTarget}
            onClose={() => setEditTarget(null)}
            onSubmit={handleEditSubmit}
          />

          <DiscountModal
            open={!!discountTarget}
            mode="single"
            initial={
              discountTarget
                ? {
                    discount_type: discountTarget.discount_type,
                    discount_value: discountTarget.discount_value,
                  }
                : null
            }
            onClose={() => setDiscountTarget(null)}
            onSubmit={handleDiscountSubmit}
            onRemove={discountTarget?.has_discount ? handleDiscountRemove : undefined}
          />
        </>
      )}

      <ShareModal
        target={{ type: "tour", id: shareOfferId }}
        link={buildTourLink(sessionKey, tour.id)}
        open={!!shareOfferId}
        onClose={() => setShareOfferId(null)}
      />
    </div>
  )
}

export default OffersList
